// Local-mode fixtures for the Authenticator settings (Settings › MFA Provider, AuthNull Authenticator)
// and the per-user phone list in Users & Access.
// Development only — wired in by mockData.js when REACT_APP_LOCAL_MODE is "true".
//
// Each entry is { pattern, respond(body) }: `pattern` is a lowercase substring
// of the request URL. Stateful, so approving, signing out or starting a
// campaign shows on the next read. The local-mode adapter always answers 200,
// so the 202 from /enrollment/campaign is a 200 here.

const iso = (msAgo) => new Date(Date.now() - msAgo).toISOString();
const MIN = 60e3;
const DAY = 86400e3;

let sendInvite = true;

let pending = [
  { loginTicket: "tkt-1", userId: 41, email: "satyam@authnull.com", deviceName: "Pixel 9", platform: "android", requestedAt: iso(4 * MIN) },
  { loginTicket: "tkt-2", userId: 57, email: "priya@authnull.com", deviceName: "iPhone 16", platform: "ios", requestedAt: iso(42 * MIN) },
];

const devicesByEmail = {
  "localdev@authnull.com": [
    { deviceId: 11, deviceName: "Pixel 8", platform: "android", appVersion: "3.5.0", osVersion: "15", enrolledAt: iso(40 * DAY), lastUsedAt: iso(2 * 3600e3), pushEnabled: true, isSelf: false, active: true, identities: ["platform_user:1"] },
    { deviceId: 12, deviceName: "iPhone 15", platform: "ios", appVersion: "3.5.0", osVersion: "18.1", enrolledAt: iso(12 * DAY), lastUsedAt: iso(3 * DAY), pushEnabled: false, isSelf: false, active: true, identities: ["platform_user:1"] },
    { deviceId: 9, deviceName: "Galaxy S21", platform: "android", appVersion: "3.4.1", osVersion: "13", enrolledAt: iso(200 * DAY), lastUsedAt: iso(90 * DAY), pushEnabled: true, isSelf: false, active: false, identities: ["platform_user:1"] },
  ],
};
const defaultDevices = (email) => [
  { deviceId: 100 + (email.length % 50), deviceName: "Pixel 9", platform: "android", appVersion: "3.5.0", osVersion: "16", enrolledAt: iso(5 * DAY), lastUsedAt: iso(20 * MIN), pushEnabled: true, isSelf: false, active: true, identities: [] },
];
const devicesFor = (email = "") => {
  const key = String(email).toLowerCase();
  if (!devicesByEmail[key]) devicesByEmail[key] = defaultDevices(key);
  return devicesByEmail[key];
};

const RESOURCES = [
  { name: "salesdb", type: "Database" },
  { name: "DC01.acme.local", type: "Domain controller" },
  { name: "vpn.acme.com", type: "RADIUS" },
  { name: "", type: "" },
];
const STATUSES = ["approved", "approved", "approved", "denied", "expired"];
const activity = Array.from({ length: 70 }, (_, i) => {
  const status = STATUSES[i % STATUSES.length];
  const created = iso(i * 37 * MIN + 5 * MIN);
  return {
    id: 5000 - i,
    flow: i % 4 === 3 ? "platform" : "ad",
    challengeId: String(9000 - i),
    email: ["satyam@authnull.com", "priya@authnull.com", "localdev@authnull.com"][i % 3],
    status,
    resolutionSource: status === "expired" ? "ttl" : "device",
    resource: RESOURCES[i % RESOURCES.length],
    location: { display: i % 5 === 0 ? "Pune, India" : "Bengaluru, India", ip: `10.2.14.${i % 250}` },
    risk: { level: i % 9 === 0 ? "HIGH" : i % 4 === 0 ? "MEDIUM" : "LOW" },
    provider: ["fcm", "fcm", "okta", "fcm", "duo"][i % 5],
    createdAt: created,
    respondedAt: status === "expired" ? undefined : created,
  };
});

let campaign = null;

let deviceSettings = {
  maxDevicesPerUser: 10, allowAppPasswordLogin: true, newDeviceCheck: "email_otp",
  browserSigninSkipsCheck: false, numberMatching: "off",
};
function setDeviceSettings(b = {}) {
  if (b.maxDevicesPerUser !== undefined && (b.maxDevicesPerUser < 1 || b.maxDevicesPerUser > 10)) {
    return { error: "maxDevicesPerUser must be between 1 and 10" };
  }
  deviceSettings = { ...deviceSettings, ...b };
  return deviceSettings;
}

function listActivity(body = {}) {
  const statuses = body.statuses || [];
  let rows = statuses.length ? activity.filter((a) => statuses.includes(a.status)) : activity;
  if (Array.isArray(body.providers)) rows = rows.filter((a) => body.providers.includes(a.provider));
  if (body.before) rows = rows.filter((a) => a.createdAt < body.before);
  const limit = Math.min(body.limit || 50, 200);
  const page = rows.slice(0, limit);
  return { items: page, ...(page.length === limit && rows.length > limit ? { nextCursor: page[page.length - 1].createdAt } : {}) };
}

function startCampaign(body = {}) {
  const targets = body.identity === "platform" ? 18 : 64;
  const skipped = body.identity === "platform" ? 11 : 29;
  campaign = {
    id: 7, identityKind: body.identity, scope: body.scope || "all", status: "running",
    targets, skipped, invited: 0, failed: 0, deadline: body.deadline, reminderDays: body.reminderDays || [],
    createdAt: iso(0), _started: Date.now(),
  };
  return { campaignId: campaign.id, status: "running", targets, skipped, toInvite: targets - skipped };
}

function getCampaign() {
  if (!campaign) return { error: "campaign not found" };
  // Pretend the background sender gets through a few invites a second.
  const toInvite = campaign.targets - campaign.skipped;
  const sent = Math.min(toInvite, Math.floor((Date.now() - campaign._started) / 400));
  campaign.failed = Math.min(2, sent);
  campaign.invited = sent - campaign.failed;
  if (sent >= toInvite) campaign.status = "done";
  return { ...campaign };
}

// The signed-in user's own factors (Settings › Tenant Configuration ›
// Multi-factor authentication). Push turns enrolled a few seconds after the QR
// is generated, standing in for the phone scanning it.
let pushEnrolledAt = null;
const mfaStatus = () => {
  const push = !!pushEnrolledAt && Date.now() >= pushEnrolledAt;
  const methods = [
    { factor_id: 1, name: "TOTP", display_name: "Authenticator App (TOTP)", status: "Active", enrolled: false, is_default: false },
    { factor_id: 2, name: "Passkey", display_name: "Biometric/Passkey Authentication", status: "Active", enrolled: true, enrolled_at: "2026-10-15T09:00:00Z", is_default: true },
    { factor_id: 10, name: "Push", display_name: "AuthNull Authenticator", status: "Active", enrolled: push, enrolled_at: push ? new Date(pushEnrolledAt).toISOString() : null, is_default: false },
  ];
  return { code: 200, status: "Success", mfa_enabled: true, mfa_default_method: "Passkey", configured_methods: methods,
    total_methods: methods.length, enrolled_methods: methods.filter((m) => m.enrolled).length };
};
// 1×1 PNG — enough to exercise the dialog without a QR library.
const MOCK_QR = "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
const selfEnroll = () => {
  if (!pushEnrolledAt) pushEnrolledAt = Date.now() + 6000;
  return { qrPngBase64: MOCK_QR, deepLink: "authnull://enroll?t=local", inviteUrl: "http://localhost:3000/enroll?t=local",
    activationCode: "K7QX-29MD", consoleHost: "localhost:3000", expiresAt: new Date(Date.now() + 15 * 60000).toISOString() };
};

export const AUTHENTICATOR_MOCKS = [
  { pattern: "/mfa/status", respond: () => mfaStatus() },
  { pattern: "/mfa/push/selfenroll", respond: () => selfEnroll() },
  // The org's push provider, so Settings › MFA Provider opens on the Authenticator.
  { pattern: "/ad/getmfaprovider", respond: () => ({ code: 200, status: "Success", provider: "authnull", source: "org", configuredForOrg: true }) },
  { pattern: "/mfa/devices/settings/get", respond: () => deviceSettings },
  { pattern: "/mfa/devices/settings/set", respond: (b) => setDeviceSettings(b || {}) },
  { pattern: "/mfa/devices/push", respond: (b) => {
    for (const list of Object.values(devicesByEmail)) {
      for (const d of list) if (d.deviceId === b?.deviceId) d.pushEnabled = !!b?.enabled;
    }
    return { success: true, pushEnabled: !!b?.enabled };
  } },
  // Browser sign-in from the app: the one-time code for authnull://signin.
  { pattern: "/device/appsignin/code", respond: () => ({ code: `local-code-${Date.now()}`, baseUrl: "http://localhost:9999", expiresIn: 60 }) },
  { pattern: "/mfa/onboarding/settings/get", respond: () => ({ code: 200, status: "Success", orgId: 1, sendInvite, provider: "authnull", inviteApplies: true }) },
  { pattern: "/mfa/onboarding/settings/set", respond: (b) => { sendInvite = !!b?.sendInvite; return { code: 200, status: "Success", orgId: 1, sendInvite, provider: "authnull", inviteApplies: true }; } },
  { pattern: "/mfa/devices/pending", respond: () => ({ pending, count: pending.length }) },
  { pattern: "/mfa/devices/approve", respond: (b) => { pending = pending.filter((p) => p.loginTicket !== b?.loginTicket); return { success: true }; } },
  { pattern: "/mfa/devices/list", respond: (b) => { const d = devicesFor(b?.email); return { devices: d, count: d.length }; } },
  { pattern: "/mfa/devices/revoke", respond: (b) => {
    for (const list of Object.values(devicesByEmail)) {
      for (const d of list) if (d.deviceId === b?.deviceId) d.active = false;
    }
    return { success: true };
  } },
  { pattern: "/mfa/activity/list", respond: (b) => listActivity(b || {}) },
  { pattern: "/mfa/enrollment/campaign/get", respond: () => getCampaign() },
  { pattern: "/mfa/enrollment/campaign", respond: (b) => startCampaign(b || {}) },
];
